// The global run board: one insert per finished run, one read per board view, both through the Supabase
// project and nothing else. Every call is bounded by an 8 s timeout and never throws; failures come back
// as outcomes the screens can show.
import type { RunResult, BoardRow, SubmitOutcome } from '../types';
import { supabase } from './supabase';
import { ensureUser, currentUserId } from './identity';
import { timed } from './timeout';

/** The range_runs_guard trigger refuses a second insert within 5 s of the last; wait it out instead. */
const MIN_GAP_MS = 5200;
let lastInsertAt = 0;

const offline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;
const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const round = (v: number, digits: number): number => { const f = 10 ** digits; return Math.round(v * f) / f; };

/** A finite number from a JSON number or a numeric string (PostgREST may serialise `numeric` either way). */
function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') { const n = Number(v); return Number.isFinite(n) ? n : null; }
  return null;
}

// ---------------------------------------------------------------- pure mapping (unit-tested)

/** Validates one raw board row from either RPC; `mod_count` is optional (the efficiency board omits it). */
export function toBoardRow(raw: unknown): BoardRow | null {
  if (!isRecord(raw)) return null;
  const { user_id, nickname, craft_id, created_at } = raw;
  if (typeof user_id !== 'string' || !user_id) return null;
  if (typeof nickname !== 'string') return null;
  if (typeof craft_id !== 'string' || !craft_id) return null;
  if (typeof created_at !== 'string' || Number.isNaN(Date.parse(created_at))) return null;
  const distanceNm = num(raw.distance_nm);
  const efficiencyIndex = num(raw.efficiency_index);
  const seed = num(raw.seed);
  const modCount = raw.mod_count === undefined || raw.mod_count === null ? 0 : num(raw.mod_count);
  if (distanceNm === null || distanceNm < 0) return null;
  if (efficiencyIndex === null || efficiencyIndex < 0) return null;
  if (seed === null || !Number.isInteger(seed)) return null;
  if (modCount === null || modCount < 0) return null;
  const name = nickname.trim().slice(0, 24);
  return {
    userId: user_id,
    nickname: name || 'Pilot',
    craftId: craft_id,
    distanceNm,
    efficiencyIndex,
    seed,
    createdAt: created_at,
    modCount: Math.floor(modCount),
  };
}

export interface RangeRunInsert {
  nickname: string;
  craft_id: string;
  seed: number;
  distance_nm: number;
  efficiency_index: number;
  fuel_used_kg: number;
  sim_hours: number;
  mod_ids: string[];
  build: string | null;
}

/** The row to insert, shaped to the table's check constraints; null when the run can never be accepted. */
export function toInsertRow(result: RunResult, nickname: string, build: string): RangeRunInsert | null {
  const distance = round(result.distanceNm, 1);
  if (!Number.isFinite(distance) || distance <= 0 || distance > 50000) return null;
  if (!Number.isInteger(result.seed) || result.seed < 20260101 || result.seed > 21001231) return null;
  if (!/^[a-z0-9-]{2,40}$/.test(result.craftId)) return null;
  const rawIndex = round(result.efficiencyIndex, 3);
  const index = Number.isFinite(rawIndex) ? Math.min(5, Math.max(0.001, rawIndex)) : 0.001;
  const name = nickname.trim().slice(0, 24).trim();
  const fuel = round(result.fuelUsedKg, 1);
  const hours = round(result.simHours, 2);
  return {
    nickname: name || 'Pilot',
    craft_id: result.craftId,
    seed: result.seed,
    distance_nm: distance,
    efficiency_index: index,
    fuel_used_kg: Number.isFinite(fuel) ? Math.max(0, fuel) : 0,
    sim_hours: Number.isFinite(hours) ? Math.max(0, hours) : 0,
    mod_ids: result.modIds.filter((m): m is string => typeof m === 'string'),
    build: build ? build.slice(0, 16) : null,
  };
}

/**
 * 'rejected' = the board will never take this row (check constraint, daily limit, policy): drop it.
 * 'error' = transient (rate gap, expired token, network): keep it and retry later.
 */
export function classifyInsertError(code: string | undefined, message: string | undefined): 'rejected' | 'error' {
  const msg = (message ?? '').toLowerCase();
  if (code === 'P0001') return msg.includes('too many submissions') ? 'error' : 'rejected';
  if (code === '23514' || code === '23502' || code === '23503' || code === '42501') return 'rejected';
  if (code && code.startsWith('22')) return 'rejected';
  return 'error';
}

// ---------------------------------------------------------------- network

async function waitForGap(): Promise<void> {
  const wait = lastInsertAt + MIN_GAP_MS - Date.now();
  if (wait > 0) await new Promise<void>((resolve) => setTimeout(resolve, wait));
}

/** Inserts the run under the anonymous user and returns its rank on today's craft board (null if that read fails). */
export async function submitRun(result: RunResult, nickname: string, build: string): Promise<SubmitOutcome> {
  const sb = supabase;
  if (!sb) return { ok: false, reason: 'unconfigured' };
  if (offline()) return { ok: false, reason: 'offline' };
  const row = toInsertRow(result, nickname, build);
  if (!row) return { ok: false, reason: 'rejected' };
  const userId = await ensureUser();
  if (!userId) return { ok: false, reason: offline() ? 'offline' : 'error' };
  try {
    await waitForGap();
    const { error } = await timed((signal) => sb.from('range_runs').insert({ user_id: userId, ...row }).abortSignal(signal));
    lastInsertAt = Date.now();
    if (error) return { ok: false, reason: classifyInsertError(error.code, error.message) };
  } catch {
    return { ok: false, reason: offline() ? 'offline' : 'error' };
  }
  return { ok: true, rank: await fetchRank(result.craftId, result.seed, row.distance_nm) };
}

async function fetchRank(craftId: string, seed: number, distanceNm: number): Promise<number | null> {
  const sb = supabase;
  if (!sb) return null;
  try {
    const { data, error } = await timed((signal) => sb.rpc('range_rank', { p_craft: craftId, p_seed: seed, p_distance: distanceNm }).abortSignal(signal));
    if (error) return null;
    const n = num(data);
    return n !== null && n >= 1 ? Math.floor(n) : null;
  } catch {
    return null;
  }
}

export type BoardResult =
  | { ok: true; rows: BoardRow[] }
  | { ok: false; reason: 'offline' | 'unconfigured' | 'error' };

async function readBoard(name: string, args: Record<string, unknown>, craftId: string | null): Promise<BoardResult> {
  const sb = supabase;
  if (!sb) return { ok: false, reason: 'unconfigured' };
  if (offline()) return { ok: false, reason: 'offline' };
  try {
    const { data, error } = await timed((signal) => sb.rpc(name, args).abortSignal(signal));
    if (error || !Array.isArray(data)) return { ok: false, reason: 'error' };
    const rows: BoardRow[] = [];
    for (const raw of data as unknown[]) {
      const row = toBoardRow(craftId !== null && isRecord(raw) ? { ...raw, craft_id: craftId } : raw);
      if (row) rows.push(row);
    }
    return { ok: true, rows };
  } catch {
    return { ok: false, reason: offline() ? 'offline' : 'error' };
  }
}

/** Best distance per pilot on one craft, today's route (seed) or all time (null). */
export function fetchBoard(craftId: string, seed: number | null, limit = 50): Promise<BoardResult> {
  return readBoard('range_board', { p_craft: craftId, p_seed: seed, p_limit: limit }, craftId);
}

/** Best efficiency index per pilot across every craft; the craft comes from the row. */
export function fetchEfficiencyBoard(seed: number | null, limit = 50): Promise<BoardResult> {
  return readBoard('range_efficiency_board', { p_seed: seed, p_limit: limit }, null);
}

/** Deletes every run this browser's user submitted. Returns the count, 0 when there is no user, null on failure. */
export async function deleteMyRuns(): Promise<number | null> {
  const sb = supabase;
  if (!sb || offline()) return null;
  const userId = await currentUserId();
  if (!userId) return 0;
  try {
    const { data, error } = await timed((signal) => sb.rpc('range_delete_mine').abortSignal(signal));
    if (error) return null;
    const n = num(data);
    return n === null ? null : Math.max(0, Math.floor(n));
  } catch {
    return null;
  }
}
